import { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Save, Send } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useMyTimesheets, useCreateTimesheet, useSubmitTimesheet } from '@/hooks/useTimesheets';
import { TIMESHEET_ENTRY_TYPES, TimesheetEntryType } from '@/types';
import { formatDate, addDays, startOfWeek, format } from '@/lib/utils';
import { cn } from '@/lib/utils';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

interface TimeEntry {
  date: string;
  entryType: TimesheetEntryType;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  description: string;
}

export function TimesheetsPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('current');
  const [weekOffset, setWeekOffset] = useState(0);
  
  // Calculate week starting date
  const today = new Date();
  const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday
  const selectedWeekStart = addDays(currentWeekStart, weekOffset * 7);
  const weekStarting = format(selectedWeekStart, 'yyyy-MM-dd');

  const { data: timesheets } = useMyTimesheets();
  const createTimesheet = useCreateTimesheet();
  const submitTimesheet = useSubmitTimesheet();

  // Find timesheet for current week or create entries
  const currentTimesheet = timesheets?.data?.find(t => t.weekStarting === weekStarting);
  
  const [entries, setEntries] = useState<Record<string, TimeEntry>>(() => {
    const initialEntries: Record<string, TimeEntry> = {};
    DAYS_OF_WEEK.forEach((_, index) => {
      const date = format(addDays(selectedWeekStart, index), 'yyyy-MM-dd');
      initialEntries[date] = {
        date,
        entryType: 'ordinary',
        startTime: '09:00',
        endTime: '17:00',
        breakMinutes: 30,
        description: '',
      };
    });
    return initialEntries;
  });

  const calculateHours = (entry: TimeEntry) => {
    if (!entry.startTime || !entry.endTime) return 0;
    const start = new Date(`2000-01-01T${entry.startTime}`);
    const end = new Date(`2000-01-01T${entry.endTime}`);
    const diffMs = end.getTime() - start.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const breakHours = entry.breakMinutes / 60;
    return Math.max(0, diffHours - breakHours);
  };

  const totalHours = Object.values(entries).reduce((sum, entry) => sum + calculateHours(entry), 0);

  const handleEntryChange = (date: string, field: string, value: unknown) => {
    setEntries(prev => ({
      ...prev,
      [date]: { ...prev[date], [field]: value },
    }));
  };

  const handleSave = async () => {
    try {
      if (currentTimesheet) {
        // Update existing timesheet entries
        // This would need the timesheet entry IDs to update
        toast({
          title: 'Success',
          description: 'Timesheet saved',
          variant: 'success',
        });
      } else {
        // Create new timesheet
        const entriesArray = Object.values(entries).filter(e => calculateHours(e) > 0).map(e => ({
          date: e.date,
          entryType: e.entryType,
          startTime: e.startTime,
          endTime: e.endTime,
          breakMinutes: e.breakMinutes,
          hours: calculateHours(e),
          description: e.description,
        }));
        
        await createTimesheet.mutateAsync({
          weekStarting,
          entries: entriesArray,
        });
        
        toast({
          title: 'Success',
          description: 'Timesheet created successfully',
          variant: 'success',
        });
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save timesheet',
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async () => {
    if (!currentTimesheet) {
      toast({
        title: 'Error',
        description: 'Please save the timesheet first',
        variant: 'destructive',
      });
      return;
    }

    try {
      await submitTimesheet.mutateAsync(currentTimesheet.id);
      toast({
        title: 'Success',
        description: 'Timesheet submitted for approval',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to submit timesheet',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'> = {
      draft: 'secondary',
      submitted: 'info',
      approved: 'success',
      rejected: 'destructive',
    };
    return <Badge variant={variants[status] || 'default'} className="capitalize">{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Timesheets</h1>
          <p className="text-sm text-muted-foreground">
            Record and submit your work hours
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setWeekOffset(prev => prev - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="px-4 py-2 border rounded-md min-w-[200px] text-center">
            <span className="font-medium">
              Week starting {formatDate(weekStarting)}
            </span>
          </div>
          <Button variant="outline" onClick={() => setWeekOffset(prev => prev + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => setWeekOffset(0)}>
            Today
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="current">Current Week</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="current" className="space-y-4">
          {currentTimesheet && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                {getStatusBadge(currentTimesheet.status)}
              </div>
              <div className="text-sm text-muted-foreground">
                Total Hours: <span className="font-bold text-foreground">{currentTimesheet.totalHours.toFixed(2)}</span>
              </div>
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="p-3 text-left text-sm font-medium">Day</th>
                      <th className="p-3 text-left text-sm font-medium">Date</th>
                      <th className="p-3 text-left text-sm font-medium">Type</th>
                      <th className="p-3 text-left text-sm font-medium">Start</th>
                      <th className="p-3 text-left text-sm font-medium">End</th>
                      <th className="p-3 text-left text-sm font-medium">Break (min)</th>
                      <th className="p-3 text-left text-sm font-medium">Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {DAYS_OF_WEEK.map((day, index) => {
                      const date = format(addDays(selectedWeekStart, index), 'yyyy-MM-dd');
                      const entry = entries[date];
                      const hours = calculateHours(entry);
                      
                      return (
                        <tr key={day} className={cn(
                          "hover:bg-muted/50",
                          hours === 0 && "bg-muted/20"
                        )}>
                          <td className="p-3 font-medium">{day}</td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {formatDate(date)}
                          </td>
                          <td className="p-3">
                            <Select
                              value={entry.entryType}
                              onValueChange={(value) => handleEntryChange(date, 'entryType', value)}
                              disabled={currentTimesheet?.status === 'submitted' || currentTimesheet?.status === 'approved'}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TIMESHEET_ENTRY_TYPES.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3">
                            <Input
                              type="time"
                              value={entry.startTime}
                              onChange={(e) => handleEntryChange(date, 'startTime', e.target.value)}
                              className="w-28"
                              disabled={currentTimesheet?.status === 'submitted' || currentTimesheet?.status === 'approved'}
                            />
                          </td>
                          <td className="p-3">
                            <Input
                              type="time"
                              value={entry.endTime}
                              onChange={(e) => handleEntryChange(date, 'endTime', e.target.value)}
                              className="w-28"
                              disabled={currentTimesheet?.status === 'submitted' || currentTimesheet?.status === 'approved'}
                            />
                          </td>
                          <td className="p-3">
                            <Input
                              type="number"
                              min="0"
                              step="5"
                              value={entry.breakMinutes}
                              onChange={(e) => handleEntryChange(date, 'breakMinutes', parseInt(e.target.value) || 0)}
                              className="w-24"
                              disabled={currentTimesheet?.status === 'submitted' || currentTimesheet?.status === 'approved'}
                            />
                          </td>
                          <td className="p-3">
                            <span className={cn(
                              "font-medium",
                              hours > 0 ? "text-foreground" : "text-muted-foreground"
                            )}>
                              {hours.toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-muted/50">
                    <tr>
                      <td colSpan={6} className="p-3 text-right font-medium">
                        Total Hours:
                      </td>
                      <td className="p-3">
                        <span className="font-bold text-lg">{totalHours.toFixed(2)}</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={createTimesheet.isPending || currentTimesheet?.status === 'submitted' || currentTimesheet?.status === 'approved'}
            >
              <Save className="mr-2 h-4 w-4" />
              Save Draft
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!currentTimesheet || submitTimesheet.isPending || currentTimesheet?.status !== 'draft'}
            >
              <Send className="mr-2 h-4 w-4" />
              Submit for Approval
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Timesheet History</CardTitle>
            </CardHeader>
            <CardContent>
              {timesheets?.data?.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No timesheets found
                </p>
              ) : (
                <div className="space-y-2">
                  {timesheets?.data?.map((timesheet) => (
                    <div
                      key={timesheet.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">
                            Week starting {formatDate(timesheet.weekStarting)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {timesheet.totalHours.toFixed(2)} hours
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(timesheet.status)}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
